/**
 * Dịch vụ › one hosting service › "Kỹ thuật (cPanel)" (subsystems/hosting/facade/adapters/admin-api):
 * the account as our records hold it (username, server, address, name servers), our state and the
 * panel's, any mismatch, the latest usage; and what support does on it in HostBill: sign in to
 * cPanel once, set the cPanel password, sync from the server, run AutoSSL. Every action asks a
 * reason and is audited on the service. Times show in Vietnam's time.
 *
 * `ResolveOperation`: an operation whose result is unknown, decided by staff (the provisioning log
 * of the service page offers it on hosting services).
 */
import { KeyRound, LogIn, RefreshCw, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { api } from '../../api';
import type { HostingResolution, HostingServiceDetail, ProvisioningOperation, ResolutionKind, UsageReading } from '../../api/areas/hosting';
import type { SubscriptionDetail } from '../../api/areas/subscriptions';
import { Badge } from '../../components/Badge';
import { Callout, Field, Section } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { KeyValue } from '../../components/KeyValue';
import { CopyText, Loading, TechnicalDetail } from '../../components/States';
import { useWrite, WriteError } from '../../components/Write';
import { useAccess } from '../../lib/access';
import { formatCount, formatDateTime } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { modulesUrl } from '../modules/ui';
import { ActionDialog, FormFooter, ReasonAction, ReasonField, selectClass, type DialogBodyProps } from '../operator/shared';

const OPERATE = 'subscription.operate';

function useCanOperate(): boolean {
  const access = useAccess();
  return Boolean(access.grants?.some((g) => g.scope === 'Global' && g.permissions.includes(OPERATE)));
}

/** A meter's reading: MB as MB, counts as counts, against its quota. */
function UsageValue({ u }: { u: UsageReading }) {
  const { t } = useTranslation('subscriptions');
  const unit = u.meter.endsWith('_MB') ? ' MB' : '';
  const quota = u.quota == null ? t('hosting.unlimited') : `${formatCount(u.quota)}${unit}`;
  const share = u.quota ? Math.min(100, Math.round((u.used * 100) / u.quota)) : null;
  return (
    <span title={formatDateTime(u.recorded_at)}>
      {formatCount(u.used)}
      {unit} / {quota}
      {share != null ? <span className={share >= 90 ? 'ml-1 text-destructive' : 'ml-1 text-muted-foreground'}>({share}%)</span> : null}
    </span>
  );
}

/** "Kỹ thuật (cPanel)" of a hosting service. */
export function HostingPanel({ service, onChanged }: { service: SubscriptionDetail; onChanged: () => void }) {
  const { t } = useTranslation('subscriptions');
  const canOperate = useCanOperate();
  const one = useApi((signal) => api.getHostingService(service.id, { signal }), [service.id]);
  if (one.status === 'loading') {
    return (
      <Section title={t('hosting.title')}>
        <Loading rows={3} />
      </Section>
    );
  }
  if (one.status === 'error') {
    return (
      <Section title={t('hosting.title')}>
        <DataError error={one.error} onRetry={one.reload} title={t('hosting.loadFailed')} />
      </Section>
    );
  }
  const d: HostingServiceDetail = one.data;
  const { account: a, service: k } = d;
  const active = k.state === 'ACTIVE';
  const loose = t as unknown as (key: string, o?: Record<string, unknown>) => string;
  const actions = canOperate ? (
    <>
      {active ? (
        <ReasonAction
          label={t('hosting.signIn')}
          icon={LogIn}
          title={t('hosting.signInTitle', { username: a.username })}
          description={t('hosting.signInIntro')}
          run={(reason, opts) => api.hostingSignIn(service.id, { reason }, opts)}
          done={(r) => (
            <span className="grid gap-2">
              <span>{t('hosting.signInReady', { at: formatDateTime(r.expires_at) })}</span>
              <a href={r.url} target="_blank" rel="noreferrer noopener" className="font-medium text-primary underline-offset-4 hover:underline">
                {t('hosting.signInOpen')}
              </a>
            </span>
          )}
        />
      ) : null}
      {active ? (
        <ActionDialog label={t('hosting.password')} icon={KeyRound} title={t('hosting.passwordTitle', { username: a.username })} onChanged={one.reload}>
          {(p) => <PasswordForm {...p} serviceId={service.id} min={d.min_password_length} />}
        </ActionDialog>
      ) : null}
      {k.provider_ref ? (
        <ReasonAction
          label={t('hosting.refresh')}
          icon={RefreshCw}
          title={t('hosting.refreshTitle', { username: a.username })}
          description={t('hosting.refreshIntro')}
          run={(reason, opts) => api.refreshHostingService(service.id, { reason }, opts)}
          done={() => t('hosting.refreshDone')}
          onChanged={onChanged}
        />
      ) : null}
      {active ? (
        <ReasonAction
          label={t('hosting.autossl')}
          icon={ShieldCheck}
          title={t('hosting.autosslTitle', { domain: a.domain })}
          description={t('hosting.autosslIntro')}
          run={(reason, opts) => api.requestHostingCertificates(service.id, { reason }, opts)}
          done={() => t('hosting.autosslDone')}
          onChanged={onChanged}
        />
      ) : null}
    </>
  ) : null;
  const addressKind = a.ip_choice.kind === 'dedicated' ? t('hosting.dedicated') : t('hosting.shared');
  // The kernel's drift is a sentence for support: shown as our state and the panel's, in words.
  const kernelState = loose(`service.${k.state}`, { defaultValue: k.state });
  const panelState = k.provider_status ? loose(`hosting.panelStatus.${k.provider_status}`, { defaultValue: k.provider_status }) : '—';
  const drift = k.drift ? (k.drift === 'MISSING_AT_PROVIDER' ? t('hosting.driftMissing') : t('hosting.driftStates', { ours: kernelState, theirs: panelState })) : t('hosting.driftNone');
  return (
    <Section title={t('hosting.title')} description={t('hosting.subtitle')} actions={actions}>
      <div className="grid gap-4">
        {k.drift ? <Callout tone="warn">{`${t('hosting.drift')}: ${drift}`}</Callout> : null}
        {k.attention ? (
          <Callout tone="warn">
            <span className="grid gap-1">
              <span>{`${t('hosting.attention')}: ${t('hosting.attentionHint')}`}</span>
              <TechnicalDetail detail={k.attention} />
            </span>
          </Callout>
        ) : null}
        <KeyValue
          columns={3}
          rows={[
            [t('hosting.username'), <CopyText key="u" value={a.username} />],
            [t('hosting.domain'), a.domain],
            [t('hosting.server'), a.server ?? t('hosting.notYet')],
            [t('hosting.ip'), a.ip ? `${a.ip} · ${addressKind}` : addressKind],
            [t('hosting.nameservers'), a.nameservers.length ? a.nameservers.join(', ') : '—'],
            [
              t('hosting.connection'),
              <Link key="c" className="text-primary hover:underline" to={modulesUrl('hosting', a.module, a.instance_key)}>
                {`${a.module}/${a.instance_key}`}
              </Link>,
            ],
            [t('hosting.kernel'), kernelState],
            [
              t('hosting.panel'),
              k.provider_status ? <Badge key="p" tone={k.provider_status === 'ACTIVE' ? 'ok' : k.provider_status === 'SUSPENDED' ? 'serious' : 'neutral'} title={k.provider_status}>{loose(`hosting.panelStatus.${k.provider_status}`, { defaultValue: k.provider_status })}</Badge> : '—',
            ],
            [t('hosting.checked'), k.provider_checked_at ? formatDateTime(k.provider_checked_at) : t('hosting.notYet')],
            [t('hosting.drift'), drift],
            [t('hosting.created'), a.confirmed_at ? formatDateTime(a.confirmed_at) : t('hosting.notYet')],
            a.released_at ? [t('hosting.released'), formatDateTime(a.released_at)] : null,
          ]}
        />
        <div className="grid gap-2">
          <h4 className="text-sm font-medium">{t('hosting.usage')}</h4>
          {d.usage.length ? (
            <KeyValue columns={3} rows={d.usage.map((u) => [loose(`hosting.meter.${u.meter}`, { defaultValue: u.meter }), <UsageValue key={u.meter} u={u} />] as const)} />
          ) : (
            <p className="text-sm text-muted-foreground">{t('hosting.usageNone')}</p>
          )}
        </div>
      </div>
    </Section>
  );
}

function PasswordForm({ close, changed, serviceId, min }: DialogBodyProps & { serviceId: string; min: number }) {
  const { t } = useTranslation('subscriptions');
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');
  const w = useWrite((b: { password: string; reason: string }, opts) => api.setHostingPassword(serviceId, b, opts));
  const done = w.state.phase === 'done';
  const valid = password.length >= min && password.length <= 128 && reason.trim().length > 0;
  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid || w.busy || done) return;
        void w.run({ password, reason: reason.trim() }).then((r) => {
          if (r) {
            setPassword('');
            changed();
          }
        });
      }}
    >
      {done ? (
        <Callout tone="ok">{t('hosting.passwordDone')}</Callout>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">{t('hosting.passwordIntro')}</p>
          <Field label={t('hosting.passwordField')} hint={t('hosting.passwordHint', { min })}>
            <Input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={min} maxLength={128} required />
          </Field>
          <ReasonField value={reason} onChange={setReason} />
        </>
      )}
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      <FormFooter busy={w.busy} disabled={!valid} submit={done ? undefined : t('op.submit')} onClose={close} />
    </form>
  );
}

const RESOLUTIONS: readonly ResolutionKind[] = ['DONE', 'NOT_DONE', 'ABANDON'];

/** "Xử lý" of an operation whose result is unknown (or a provider job that never ends). */
export function ResolveOperation({ operation, serviceId, onChanged }: { operation: Pick<ProvisioningOperation, 'id' | 'op'>; serviceId: string; onChanged: () => void }) {
  const { t } = useTranslation('subscriptions');
  const loose = t as unknown as (key: string, o?: Record<string, unknown>) => string;
  const op = loose(`opName.${operation.op}`, { defaultValue: operation.op });
  return (
    <ActionDialog label={t('hosting.resolve')} title={t('hosting.resolveTitle', { op })} variant="outline" onChanged={onChanged}>
      {(p) => <ResolveForm {...p} operation={operation} serviceId={serviceId} />}
    </ActionDialog>
  );
}

function ResolveForm({ close, changed, operation, serviceId }: DialogBodyProps & { operation: Pick<ProvisioningOperation, 'id' | 'op'>; serviceId: string }) {
  const { t } = useTranslation('subscriptions');
  const [resolution, setResolution] = useState<ResolutionKind>('DONE');
  const [providerRef, setProviderRef] = useState('');
  const [reason, setReason] = useState('');
  const create = operation.op === 'create';
  // A create's account at the panel is the username recorded (`hosting.accounts`) unless staff name another.
  const account = useApi((signal) => (create ? api.getHostingService(serviceId, { signal }) : Promise.resolve(null)), [serviceId, create]);
  const username = account.status === 'success' ? (account.data?.account.username ?? null) : null;
  // The whole body: an Idempotency-Key per distinct request (another choice is a new one).
  const w = useWrite((b: HostingResolution, opts) => api.resolveHostingOperation(operation.id, b, opts));
  const done = w.state.phase === 'done';
  const valid = reason.trim().length > 0 && reason.trim().length <= 500;
  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        const provider_ref = resolution === 'DONE' && create ? providerRef.trim() || null : null;
        if (valid && !w.busy && !done) void w.run({ resolution, provider_ref, reason: reason.trim() }).then((r) => r && changed());
      }}
    >
      {done ? (
        <Callout tone="ok">{t('hosting.resolveDone')}</Callout>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">{t('hosting.resolveIntro')}</p>
          <Field label={t('hosting.resolveChoice')}>
            <select className={selectClass} value={resolution} onChange={(e) => setResolution(e.target.value as ResolutionKind)}>
              {RESOLUTIONS.map((r) => (
                <option key={r} value={r}>
                  {t(`hosting.resolution.${r}`)}
                </option>
              ))}
            </select>
          </Field>
          {resolution === 'DONE' && create ? (
            <Field label={t('hosting.providerRef')} hint={username ? t('hosting.providerRefHint', { username }) : undefined}>
              <Input value={providerRef} onChange={(e) => setProviderRef(e.target.value)} className="font-mono" maxLength={200} placeholder={username ?? undefined} />
            </Field>
          ) : null}
          <ReasonField value={reason} onChange={setReason} />
        </>
      )}
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      <FormFooter busy={w.busy} disabled={!valid} submit={done ? undefined : t('op.submit')} onClose={close} danger={resolution === 'ABANDON'} />
    </form>
  );
}
